import {
  Heart,
  Activity,
  Droplets,
  Wind,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { MonitoredPatient, VitalStatus, VitalTrend } from "@/hooks/useRemoteMonitoring";
import { RpmBillingTracker } from "./RpmBillingTracker";

interface PatientVitalsCardProps {
  patient: MonitoredPatient;
  onViewDetails: () => void;
  reviewTimer?: {
    elapsedSeconds: number;
    isActive: boolean;
    isEligible: boolean;
    onStart: () => void;
    onStop: () => void;
    onReset: () => void;
    onClaimGenerated: () => void;
  };
}

const statusColors = {
  normal: "text-success",
  warning: "text-warning",
  critical: "text-destructive",
};

const statusBgColors = {
  normal: "bg-success/10",
  warning: "bg-warning/10",
  critical: "bg-destructive/10",
};

function TrendIcon({ trend }: { trend: string }) {
  if (trend === "up") return <TrendingUp className="h-3 w-3" />;
  if (trend === "down") return <TrendingDown className="h-3 w-3" />;
  return <Minus className="h-3 w-3" />;
}

function VitalMetric({
  icon: Icon,
  label,
  value,
  unit,
  trend,
  status,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  unit: string;
  trend: VitalTrend;
  status: VitalStatus;
}) {
  return (
    <div className={cn("rounded-lg p-3", statusBgColors[status])}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={cn("h-4 w-4", statusColors[status])} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className={cn("text-lg font-bold", statusColors[status])}>{value}</span>
        <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
      <div className={cn("flex items-center gap-1 text-xs mt-1", 
        trend === "up" ? "text-destructive" : trend === "down" ? "text-success" : "text-muted-foreground"
      )}>
        <TrendIcon trend={trend} />
        {trend === "stable" ? "Stable" : trend === "up" ? "Rising" : "Falling"}
      </div>
    </div>
  );
}

export function PatientVitalsCard({ patient, onViewDetails, reviewTimer }: PatientVitalsCardProps) {
  const stepsProgress = (patient.vitals.steps.value / patient.vitals.steps.goal) * 100;

  return (
    <Card className={cn(
      "transition-all hover:shadow-lg",
      patient.alerts > 0 && "border-l-4 border-l-destructive"
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {patient.name.split(" ").map(n => n[0]).join("")}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{patient.name}</h3>
                {patient.alerts > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {patient.alerts}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Age {patient.age} • {patient.conditions.length > 0 ? patient.conditions.join(", ") : "No conditions listed"}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-xs">
              <span className="text-lg">{patient.device.icon}</span>
              {patient.device.connected ? (
                <span className="flex items-center gap-1 text-success">
                  <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
                  Online
                </span>
              ) : (
                <span className="text-muted-foreground">Offline</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{patient.device.lastSync}</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Main Vitals Grid */}
        <div className="grid grid-cols-2 gap-3">
          <VitalMetric
            icon={Heart}
            label="Heart Rate"
            value={patient.vitals.heartRate.value}
            unit={patient.vitals.heartRate.unit}
            trend={patient.vitals.heartRate.trend}
            status={patient.vitals.heartRate.status}
          />
          <VitalMetric
            icon={Activity}
            label="Blood Pressure"
            value={`${patient.vitals.bloodPressure.systolic}/${patient.vitals.bloodPressure.diastolic}`}
            unit={patient.vitals.bloodPressure.unit}
            trend={patient.vitals.bloodPressure.trend}
            status={patient.vitals.bloodPressure.status}
          />
          <VitalMetric
            icon={Droplets}
            label="Blood Glucose"
            value={patient.vitals.bloodGlucose.value}
            unit={patient.vitals.bloodGlucose.unit}
            trend={patient.vitals.bloodGlucose.trend}
            status={patient.vitals.bloodGlucose.status}
          />
          <VitalMetric
            icon={Wind}
            label="SpO₂"
            value={patient.vitals.oxygenSaturation.value}
            unit={patient.vitals.oxygenSaturation.unit}
            trend={patient.vitals.oxygenSaturation.trend}
            status={patient.vitals.oxygenSaturation.status}
          />
        </div>

        {/* Activity */}
        <div className="rounded-lg bg-muted/50 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Daily Steps</span>
            <span className="text-sm text-muted-foreground">
              {patient.vitals.steps.value.toLocaleString()} / {patient.vitals.steps.goal.toLocaleString()}
            </span>
          </div>
          <Progress value={Math.min(stepsProgress, 100)} className="h-2" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Sleep: {patient.vitals.sleep.value}h ({patient.vitals.sleep.quality})</span>
            <span>{Math.round(stepsProgress)}% of goal</span>
          </div>
        </div>

        {/* RPM Billing Timer */}
        {reviewTimer && (
          <RpmBillingTracker
            memberId={patient.memberId}
            patientName={patient.name}
            elapsedSeconds={reviewTimer.elapsedSeconds}
            isActive={reviewTimer.isActive}
            isEligible={reviewTimer.isEligible}
            onStart={reviewTimer.onStart}
            onStop={reviewTimer.onStop}
            onReset={reviewTimer.onReset}
            onClaimGenerated={reviewTimer.onClaimGenerated}
          />
        )}

        <Button variant="outline" className="w-full" onClick={onViewDetails}>
          View Full Details
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
